import React, {useState} from 'react';
import {Alert, Box, Button, Link, TextField, Typography} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import {requestPasswordReset} from '../services/authService';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';
import {looksLikeEmail} from '../utilities/passwordReset';

// "Forgot your password?": asks for a link to be emailed. What it says afterwards is the same whether or not an account has that address,
// because the server answers the same: nobody can use this page to find out who has an account.
export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [sentTo, setSentTo] = useState('');
    const [busy, setBusy] = useState(false);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError('');
        if (!looksLikeEmail(email)) { setError('Please enter a valid email address'); return; }

        setBusy(true);
        try {
            await requestPasswordReset(email.trim());
            setSentTo(email.trim());
        } catch (failure) {
            setError(isNetworkError(failure) ? 'Network error. Please try again later.' : apiErrorMessage(failure, "That didn't work. Try again."));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box component="form" onSubmit={submit} noValidate sx={{display: 'flex', flexDirection: 'column', maxWidth: 400, margin: '48px auto 0', padding: 3, gap: 2,
            border: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main', borderRadius: 2, bgcolor: 'background.paper'}}>
            <Typography variant="h5" component="h1">Forgot your password?</Typography>

            {sentTo ? (
                <>
                    <Alert severity="success">
                        If an account uses {sentTo}, we have emailed it a link to choose a new password. It works for an hour, and once.
                    </Alert>
                    <Typography variant="body2" color="text.secondary">Nothing there? Check your spam folder, or ask again in a few minutes.</Typography>
                </>
            ) : (
                <>
                    <Typography variant="body2" color="text.secondary">Enter your email address and we will send you a link to choose a new one.</Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" autoFocus fullWidth disabled={busy}/>
                    <Button type="submit" variant="contained" disabled={busy}>{busy ? 'Sending…' : 'Email me a link'}</Button>
                </>
            )}

            <Link component={RouterLink} to="/login" variant="body2">Back to sign in</Link>
        </Box>
    );
}
